import { CUSTOM_OVERRIDE_TOLERANCE } from '@ovComponents/4-module/widgets/portfolio/components/allocationTolerances';
import {
  type SolveStatus,
  SolveStatuses,
} from '@ovComponents/4-module/widgets/portfolio/components/lockedMatrixRedistribute';
import {
  fractionFromAmountCents,
  fractionToAmountCents,
} from '@utils/allocationUnits';

export type CellEditPatch = {
  lockedCells?: Map<string, number>;
  lockedAmountCents?: Map<string, number>;
  uploadedCells?: Set<string>;
  draft: Map<string, number>;
  solveStatus: SolveStatus | null;
};

export type CellEditInput = {
  key: string;
  fraction: number;
  amountCents?: number;
  mv: number;
  original: number;
  lockedCells: Map<string, number>;
  lockedAmountCents: Map<string, number>;
  uploadedCells?: Set<string>;
  draft: Map<string, number>;
  solveStatus: SolveStatus | null;
};

type CellLockInput = Pick<
  CellEditInput,
  'key' | 'lockedCells' | 'lockedAmountCents' | 'uploadedCells'
>;

// `draft` is only valid for the exact lock set it was solved against; discard it
// wholesale rather than risk a stale partial draft satisfying `hasChanges` with an
// incomplete column payload.
const clearDraftPatch = () => ({
  draft: new Map<string, number>(),
  solveStatus: null,
});

// Any lock change drops the uploaded mark, so uploadedCells stays a subset of lockedCells.
const withoutUploadedMark = ({ key, uploadedCells }: CellLockInput) => {
  const nextUploaded = new Set(uploadedCells);
  nextUploaded.delete(key);
  return nextUploaded;
};

export const unlockPatch = (input: CellLockInput) => {
  const { key, lockedCells, lockedAmountCents } = input;
  const nextLocks = new Map(lockedCells);
  nextLocks.delete(key);
  const nextLockCents = new Map(lockedAmountCents);
  nextLockCents.delete(key);

  // Removing a lock changes the constraints the last solve was run
  // under, so — regardless of whether other locks remain — the solved
  // draft is no longer valid and must be discarded (see clearDraftPatch).
  return {
    lockedCells: nextLocks,
    lockedAmountCents: nextLockCents,
    uploadedCells: withoutUploadedMark(input),
    draft: new Map<string, number>(),
    solveStatus: null,
  };
};

const lockPatch = (
  input: CellEditInput,
  canonicalFraction: number,
  lockAmountCents: number,
) => {
  const { key, lockedCells, lockedAmountCents } = input;
  const nextLocks = new Map(lockedCells);
  const nextLockCents = new Map(lockedAmountCents);
  nextLocks.set(key, canonicalFraction);
  nextLockCents.set(key, lockAmountCents);

  return {
    lockedCells: nextLocks,
    lockedAmountCents: nextLockCents,
    uploadedCells: withoutUploadedMark(input),
    draft: new Map<string, number>(),
    solveStatus: null,
  };
};

const clearLockPatch = (input: CellEditInput) => {
  const wasLocked = input.lockedCells.has(input.key);

  if (
    !wasLocked &&
    input.solveStatus === SolveStatuses.OK &&
    input.draft.has(input.key)
  ) {
    return clearDraftPatch();
  }

  return unlockPatch(input);
};

const resetToPersistedPatch = (input: CellEditInput) => {
  if (!input.lockedCells.has(input.key) && !input.draft.has(input.key)) {
    return null;
  }
  return clearLockPatch(input);
};

const displayedCentsOf = (input: CellEditInput, persistedCents: number) => {
  const lockedCents = input.lockedAmountCents.get(input.key);
  if (lockedCents !== undefined) return lockedCents;

  const stagedFraction = input.lockedCells.get(input.key);
  if (stagedFraction !== undefined) {
    return fractionToAmountCents(stagedFraction, input.mv);
  }

  const solvedFraction =
    input.solveStatus === SolveStatuses.OK
      ? input.draft.get(input.key)
      : undefined;
  if (solvedFraction !== undefined) {
    return fractionToAmountCents(solvedFraction, input.mv);
  }

  return persistedCents;
};

const displayedFractionOf = (input: CellEditInput) =>
  input.lockedCells.get(input.key) ??
  (input.solveStatus === SolveStatuses.OK
    ? input.draft.get(input.key)
    : undefined) ??
  input.original;

const resolveAmountEditPatch = (
  input: CellEditInput,
  amountCents: number,
  persistedCents: number,
) => {
  if (amountCents === displayedCentsOf(input, persistedCents)) return null;

  if (amountCents === persistedCents) {
    return resetToPersistedPatch(input);
  }

  return lockPatch(
    input,
    fractionFromAmountCents(amountCents, input.mv),
    amountCents,
  );
};

const resolveFractionEditPatch = (input: CellEditInput) => {
  if (Math.abs(input.fraction - input.original) <= CUSTOM_OVERRIDE_TOLERANCE) {
    return resetToPersistedPatch(input);
  }

  if (
    Math.abs(input.fraction - displayedFractionOf(input)) <=
    CUSTOM_OVERRIDE_TOLERANCE
  ) {
    return null;
  }

  const cents = fractionToAmountCents(input.fraction, input.mv);
  return lockPatch(input, fractionFromAmountCents(cents, input.mv), cents);
};

// Returns null when the edit is a no-op; the hook dispatches the patch otherwise.
export const resolveCellEdit = (input: CellEditInput): CellEditPatch | null => {
  const persistedCents = fractionToAmountCents(input.original, input.mv);

  if (input.amountCents !== undefined) {
    return resolveAmountEditPatch(input, input.amountCents, persistedCents);
  }

  return resolveFractionEditPatch(input);
};
